// Column labels for the .inp sections the property panel shows, in real
// column order. OUTFALLS is the one section where a field's value changes
// the row's actual LENGTH — a FREE/NORMAL outfall has no stage-data column
// at all, so its columns are resolved from the row itself (`outfallColumns`)
// rather than a fixed list; getting that wrong misreads Tide Gate as
// Stage/Table. INFILTRATION's columns (and label) switch on the project's
// own [OPTIONS] INFILTRATION method instead, since that value lives OUTSIDE
// the row (`infiltrationColumns`) — the 5 raw values on disk are always
// correct regardless, only the label shown depends on this.

import type { LinkKind, NodeKind } from "./types.ts";

// A [section, label] pair absent here stays a plain text/number field —
// most columns are free values, only a real closed set belongs here.

// Every real cross-section shape — this port's own conduit hydraulics only
// compute CIRCULAR, but the property panel shows/edits the real .inp data
// faithfully, not just what this port's engine can simulate.
const XSECT_SHAPES = [
  "DUMMY",
  "CIRCULAR",
  "FILLED_CIRCULAR",
  "RECT_CLOSED",
  "RECT_OPEN",
  "TRAPEZOIDAL",
  "TRIANGULAR",
  "PARABOLIC",
  "POWER",
  "RECT_TRIANGULAR",
  "RECT_ROUND",
  "MODBASKETHANDLE",
  "HORIZ_ELLIPSE",
  "VERT_ELLIPSE",
  "ARCH",
  "EGG",
  "HORSESHOE",
  "GOTHIC",
  "CATENARY",
  "SEMIELLIPTICAL",
  "BASKETHANDLE",
  "SEMICIRCULAR",
  "IRREGULAR",
  "CUSTOM",
  "FORCE_MAIN",
  "STREET",
] as const;

const COLUMN_OPTIONS: Record<string, Record<string, readonly string[]>> = {
  SUBAREAS: { "Route To": ["OUTLET", "IMPERV", "PERV"] },
  PUMPS: { Status: ["OFF", "ON"] },
  XSECTIONS: { Shape: XSECT_SHAPES },
  // The raw file token is "PARABOLIC", not this port's own internal
  // "PARABOLOID" TypeScript name.
  STORAGE: { Shape: ["TABULAR", "FUNCTIONAL", "CYLINDRICAL", "CONICAL", "PARABOLIC", "PYRAMIDAL"] },
  DIVIDERS: { "Divider Type": ["CUTOFF", "TABULAR", "WEIR", "OVERFLOW"] },
  ORIFICES: {
    "Flap Gate": ["NO", "YES"],
    "Orifice Type": ["SIDE", "BOTTOM"],
  },
  WEIRS: {
    "Flap Gate": ["NO", "YES"],
    "Weir Type": ["TRANSVERSE", "SIDEFLOW", "V-NOTCH", "TRAPEZOIDAL", "ROADWAY"],
  },
  OUTFALLS: {
    "Outfall Type": ["FREE", "NORMAL", "FIXED", "TIDAL", "TIMESERIES"],
  },
  // The real Rating Type token is a single combined "TABULAR/DEPTH"-shaped
  // value, not two separate fields.
  OUTLETS: {
    "Flap Gate": ["NO", "YES"],
    "Rating Type": ["TABULAR/DEPTH", "TABULAR/HEAD", "FUNCTIONAL/DEPTH", "FUNCTIONAL/HEAD"],
  },
  RAINGAGES: {
    Format: ["INTENSITY", "VOLUME", "CUMULATIVE"],
    "Source Type": ["TIMESERIES", "FILE"],
  },
};

/** `undefined` for a column with no closed set — never a fallback empty array, so callers can tell "no options" apart from "an enum with zero values". */
export function columnOptions(section: string, label: string): readonly string[] | undefined {
  return COLUMN_OPTIONS[section]?.[label];
}

/**
 * A field's presence here is also the signal PropertyPanel.tsx uses to
 * reject a non-numeric edit instead of saving it silently — absent means
 * free text, unvalidated. "flow" uses `flowUnits` verbatim (already the
 * real unit). "perHour"/"days"/"hours" never convert with US/SI at all.
 */
export type UnitKind =
  | "length"
  | "landarea"
  | "flow"
  | "percent"
  | "depth"
  | "rate"
  | "perHour"
  | "days"
  | "hours"
  | "dimensionless";

/**
 * Several real columns are deliberately ABSENT here (stay free text, no
 * validation) because their true type depends on ANOTHER value in the same
 * row — forcing numeric validation on them would reject real, valid data:
 * - STORAGE "Coeff./Curve A"/"Coeff. B"/"Coeff. C": a curve NAME for
 *   Shape=TABULAR, unrelated physical magnitudes otherwise.
 * - DIVIDERS "Param 1"-"Param 5" and OUTLETS "Param 1"/"Param 2": meaning
 *   changes entirely with Type (a curve NAME for TABULAR, real values
 *   otherwise).
 * - XSECTIONS "Geom1"-"Geom4": real lengths for most shapes, but a Shape
 *   Curve NAME for CUSTOM, a Transect NAME for IRREGULAR, and Geom2 is
 *   either a Hazen-Williams C-factor or a roughness length for FORCE_MAIN
 *   depending on `[OPTIONS] FORCE_MAIN_EQUATION`.
 * - OUTFALLS "Stage/Table/Timeseries": a curve/timeseries NAME for
 *   TIDAL/TIMESERIES, only actually numeric for FIXED.
 * - SUBCATCHMENTS "Curb Length": genuinely independent of the project's
 *   US/SI system — this port never applies a length conversion to it.
 * - WEIRS "Discharge Coeff."/"End Coeff.": calibrated against the raw
 *   length unit, never run through unit conversion.
 * - RAINGAGES "Interval": mixed decimal-hours OR "H:MM", not a plain number.
 */
const NUMERIC_UNIT: Record<string, Record<string, UnitKind>> = {
  JUNCTIONS: {
    "Invert Elevation": "length",
    "Max Depth": "length",
    "Init Depth": "length",
    "Surcharge Depth": "length",
    "Ponded Area": "landarea",
  },
  OUTFALLS: { "Invert Elevation": "length" },
  CONDUITS: {
    Length: "length",
    Roughness: "dimensionless",
    "Inlet Offset": "length",
    "Outlet Offset": "length",
    "Init Flow": "flow",
    "Max Flow": "flow",
  },
  PUMPS: { "Startup Depth": "length", "Shutoff Depth": "length" },
  ORIFICES: {
    "Crest Height": "length",
    "Discharge Coeff.": "dimensionless",
    "Open/Close Time": "hours",
  },
  WEIRS: { "Crest Height": "length", "End Contractions": "dimensionless" },
  OUTLETS: { "Inlet Offset": "length" },
  XSECTIONS: { Barrels: "dimensionless", "Culvert Code": "dimensionless" },
  SUBCATCHMENTS: {
    Area: "landarea",
    Width: "length",
    "%Imperv": "percent",
    "%Slope": "percent",
  },
  SUBAREAS: {
    "N-Imperv": "dimensionless",
    "N-Perv": "dimensionless",
    "S-Imperv": "depth",
    "S-Perv": "depth",
    "%Zero-Imperv": "percent",
    "%Routed": "percent",
  },
  RAINGAGES: { SCF: "dimensionless" },
};

/** Switches on the project's own [OPTIONS] INFILTRATION method, same as `INFILTRATION_COLUMNS` — a Green-Ampt "Conductivity" is a rate (in/hr), a Horton "Decay Constant" is per-hour data, never the same shape. */
const INFILTRATION_UNIT: Record<string, Record<string, UnitKind>> = {
  HORTON: {
    "Max Rate": "rate",
    "Min Rate": "rate",
    "Decay Constant": "perHour",
    "Dry Time": "days",
    "Max Infiltration": "depth",
  },
  MODIFIED_HORTON: {
    "Max Rate": "rate",
    "Min Rate": "rate",
    "Decay Constant": "perHour",
    "Dry Time": "days",
    "Max Infiltration": "depth",
  },
  GREEN_AMPT: { "Suction Head": "depth", Conductivity: "rate", "Initial Deficit": "dimensionless" },
  MODIFIED_GREEN_AMPT: {
    "Suction Head": "depth",
    Conductivity: "rate",
    "Initial Deficit": "dimensionless",
  },
  // Conductivity here is real but deprecated (curvenumberinfiltrationpara.htm:
  // "no longer used") — still shown with its real unit, same as any other
  // legacy-but-still-on-disk value this codebase never silently drops.
  CURVE_NUMBER: { "Curve Number": "dimensionless", Conductivity: "rate", "Drying Time": "days" },
};

/** `undefined` for a field with no verified real unit (stays free text, unvalidated) — same "absence is a real, deliberate state, never a fallback" convention as `columnOptions`. */
export function numericUnit(
  section: string,
  label: string,
  infiltrationMethod?: string
): UnitKind | undefined {
  if (section === "INFILTRATION") {
    return (
      INFILTRATION_UNIT[(infiltrationMethod ?? "HORTON").toUpperCase()]?.[label] ??
      INFILTRATION_UNIT.HORTON[label]
    );
  }
  return NUMERIC_UNIT[section]?.[label];
}

/** A function instead of a fixed list for OUTFALLS (column count depends on another field in the row) or INFILTRATION (depends on the project's own [OPTIONS] INFILTRATION method, not the row). */
interface SectionSchema {
  label: string | ((infiltrationMethod?: string) => string);
  columns: string[] | ((row: string[], infiltrationMethod?: string) => string[]);
}

const OUTFALL_STAGE_DATA_TYPES = new Set(["FIXED", "TIDAL", "TIMESERIES"]);

/** Shared with `properties.ts`'s own `setPropertyValue`, which must RESTRUCTURE the row (not just overwrite a cell) when editing this column changes which shape applies — the two can't drift apart. */
export function outfallHasStageData(outfallType: string): boolean {
  return OUTFALL_STAGE_DATA_TYPES.has(outfallType.toUpperCase());
}

export const OUTFALL_TYPE_COLUMN_INDEX = 1;

function outfallColumns(row: string[]): string[] {
  const hasStageData = outfallHasStageData(row[2] ?? "");
  return hasStageData
    ? ["Invert Elevation", "Outfall Type", "Stage/Table/Timeseries", "Tide Gate"]
    : ["Invert Elevation", "Outfall Type", "Tide Gate"];
}

// [INFILTRATION] rows always hold the same 5 raw values on disk — only the
// LABEL shown depends on the project's own method. Modified Horton/
// Green-Ampt share their base method's labels — "Modified" only changes
// the underlying formula, not which columns exist.
const INFILTRATION_COLUMNS: Record<string, string[]> = {
  HORTON: ["Max Rate", "Min Rate", "Decay Constant", "Dry Time", "Max Infiltration"],
  MODIFIED_HORTON: ["Max Rate", "Min Rate", "Decay Constant", "Dry Time", "Max Infiltration"],
  GREEN_AMPT: ["Suction Head", "Conductivity", "Initial Deficit"],
  MODIFIED_GREEN_AMPT: ["Suction Head", "Conductivity", "Initial Deficit"],
  CURVE_NUMBER: ["Curve Number", "Conductivity", "Drying Time"],
};

const INFILTRATION_METHOD_LABELS: Record<string, string> = {
  HORTON: "Horton",
  MODIFIED_HORTON: "Modified Horton",
  GREEN_AMPT: "Green Ampt",
  MODIFIED_GREEN_AMPT: "Modified Green Ampt",
  CURVE_NUMBER: "Curve Number",
};

/** Defaults to Horton — SWMM's own default when `[OPTIONS] INFILTRATION` is absent or unrecognized. */
function infiltrationColumns(infiltrationMethod: string | undefined): string[] {
  return (
    INFILTRATION_COLUMNS[(infiltrationMethod ?? "HORTON").toUpperCase()] ??
    INFILTRATION_COLUMNS.HORTON
  );
}

function infiltrationMethodLabel(infiltrationMethod: string | undefined): string {
  return (
    INFILTRATION_METHOD_LABELS[(infiltrationMethod ?? "HORTON").toUpperCase()] ??
    INFILTRATION_METHOD_LABELS.HORTON
  );
}

export const SECTION_SCHEMAS: Record<string, SectionSchema> = {
  JUNCTIONS: {
    label: "Junction",
    columns: ["Invert Elevation", "Max Depth", "Init Depth", "Surcharge Depth", "Ponded Area"],
  },
  OUTFALLS: {
    label: "Outfall",
    columns: outfallColumns,
  },
  STORAGE: {
    label: "Storage",
    columns: [
      "Invert Elevation",
      "Max Depth",
      "Init Depth",
      "Shape",
      "Coeff./Curve A",
      "Coeff. B",
      "Coeff. C",
      "Evap Fraction",
      "Suction Head",
      "Conductivity",
      "Initial Deficit",
    ],
  },
  DIVIDERS: {
    label: "Divider",
    columns: [
      "Elevation",
      "Diverted Link",
      "Divider Type",
      "Param 1",
      "Param 2",
      "Param 3",
      "Param 4",
      "Param 5",
    ],
  },
  CONDUITS: {
    label: "Conduit",
    columns: [
      "From Node",
      "To Node",
      "Length",
      "Roughness",
      "Inlet Offset",
      "Outlet Offset",
      "Init Flow",
      "Max Flow",
    ],
  },
  PUMPS: {
    label: "Pump",
    columns: ["From Node", "To Node", "Pump Curve", "Status", "Startup Depth", "Shutoff Depth"],
  },
  ORIFICES: {
    label: "Orifice",
    columns: [
      "From Node",
      "To Node",
      "Orifice Type",
      "Crest Height",
      "Discharge Coeff.",
      "Flap Gate",
      "Open/Close Time",
    ],
  },
  WEIRS: {
    label: "Weir",
    columns: [
      "From Node",
      "To Node",
      "Weir Type",
      "Crest Height",
      "Discharge Coeff.",
      "Flap Gate",
      "End Contractions",
      "End Coeff.",
    ],
  },
  // No real example to verify against — column meaning from the format docs.
  OUTLETS: {
    label: "Outlet",
    columns: [
      "From Node",
      "To Node",
      "Outlet Offset",
      "Rating Type",
      "Param 1",
      "Param 2",
      "Flap Gate",
    ],
  },
  XSECTIONS: {
    label: "Cross-section",
    columns: ["Shape", "Geom1", "Geom2", "Geom3", "Geom4", "Barrels", "Culvert Code"],
  },
  SUBCATCHMENTS: {
    label: "Subcatchment",
    columns: [
      "Raingage",
      "Outlet",
      "Area",
      "%Imperv",
      "Width",
      "%Slope",
      "Curb Length",
      "Snow Pack",
    ],
  },
  SUBAREAS: {
    label: "Subareas",
    columns: ["N-Imperv", "N-Perv", "S-Imperv", "S-Perv", "%Zero-Imperv", "Route To", "%Routed"],
  },
  INFILTRATION: {
    label: (infiltrationMethod) => `Infiltration (${infiltrationMethodLabel(infiltrationMethod)})`,
    columns: (_row, infiltrationMethod) => infiltrationColumns(infiltrationMethod),
  },
  RAINGAGES: {
    label: "Raingage",
    columns: ["Format", "Interval", "SCF", "Source Type", "Source Name"],
  },
};

const NODE_KIND_TO_SECTION: Record<NodeKind, string> = {
  junction: "JUNCTIONS",
  outfall: "OUTFALLS",
  storage: "STORAGE",
  divider: "DIVIDERS",
};

const LINK_KIND_TO_SECTION: Record<LinkKind, string> = {
  conduit: "CONDUITS",
  pump: "PUMPS",
  orifice: "ORIFICES",
  weir: "WEIRS",
  outlet: "OUTLETS",
};

/** The two Records above are typed against `NodeKind`/`LinkKind` directly, so adding a kind without also mapping it to a section here is a compile error. */
type ElementKind = NodeKind | LinkKind | "subcatchment" | "raingage";

export function sectionsForElement(
  kind: ElementKind,
  elementKind: "node" | "link" | "subcatchment" | "raingage"
): string[] {
  // kind/elementKind are independent parameters, so TS can't correlate
  // which slice of ElementKind applies here — trusted the same way an
  // ElementRef itself always upholds it (a node's own kind is a NodeKind).
  if (elementKind === "node") return [NODE_KIND_TO_SECTION[kind as NodeKind]];
  if (elementKind === "link") return [LINK_KIND_TO_SECTION[kind as LinkKind], "XSECTIONS"];
  if (elementKind === "subcatchment") return ["SUBCATCHMENTS", "SUBAREAS", "INFILTRATION"];
  return ["RAINGAGES"];
}

/** Sections whose every row is "owned" by exactly one element (row[0] = its own name) beyond what `sectionsForElement` already covers — safe to duplicate wholesale when copying, must be cleared wholesale when deleting. Anything not listed here is a shared resource an element only points AT by name, never owns. */
const EXTRA_OWNED_SECTIONS: Record<"node" | "link" | "subcatchment" | "raingage", string[]> = {
  node: ["COORDINATES", "TREATMENT", "DWF", "INFLOWS", "RDII"],
  link: ["VERTICES", "LOSSES", "INLET_USAGE"],
  subcatchment: ["POLYGONS", "COVERAGES", "LID_USAGE", "GROUNDWATER", "LOADINGS", "GWF"],
  raingage: ["SYMBOLS"],
};

/** Shared by clipboard.ts (duplicate wholesale on copy) and editNetwork.ts's own `deleteElement` (clear wholesale on delete) so the two can never drift apart — missing a section here leaves dangling rows behind that reference an element no longer there, an .inp real SWMM would reject on load. */
export function ownedSections(
  kind: ElementKind,
  elementKind: "node" | "link" | "subcatchment" | "raingage"
): string[] {
  return [...sectionsForElement(kind, elementKind), ...EXTRA_OWNED_SECTIONS[elementKind]];
}
